// Major US cities by state — 3-5 largest cities per state, by population.
// Used to provide instant results when students type a US state name like
// "Texas" or "California" instead of a specific city. Coordinates from
// public domain census + Wikipedia data, accurate to 4 decimals (~10m).
//
// Each city is shaped to mimic an Open-Meteo geocoding result so it can be
// passed straight into pickResult() / buildLocationSubtitle() without
// special-case logic.
const US = "United States";
const c = (id, name, admin1, latitude, longitude) => ({
  id: `state-${id}`,
  name,
  admin1,
  country: US,
  country_code: "US",
  latitude,
  longitude,
  isVirtual: true,
});

let nextId = 1;
const make = (admin1, cities) =>
  cities.map(([name, lat, lng]) => c(nextId++, name, admin1, lat, lng));

export const STATE_MAJOR_CITIES = {
  Alabama: make("Alabama", [
    ["Birmingham", 33.5186, -86.8104],
    ["Montgomery", 32.3668, -86.2999],
    ["Mobile", 30.6954, -88.0399],
    ["Huntsville", 34.7304, -86.5861],
  ]),
  Alaska: make("Alaska", [
    ["Anchorage", 61.2181, -149.9003],
    ["Fairbanks", 64.8378, -147.7164],
    ["Juneau", 58.3019, -134.4197],
  ]),
  Arizona: make("Arizona", [
    ["Phoenix", 33.4484, -112.0740],
    ["Tucson", 32.2226, -110.9747],
    ["Mesa", 33.4152, -111.8315],
    ["Flagstaff", 35.1983, -111.6513],
  ]),
  Arkansas: make("Arkansas", [
    ["Little Rock", 34.7465, -92.2896],
    ["Fort Smith", 35.3859, -94.3985],
    ["Fayetteville", 36.0822, -94.1719],
  ]),
  California: make("California", [
    ["Los Angeles", 34.0522, -118.2437],
    ["San Diego", 32.7157, -117.1611],
    ["San Jose", 37.3382, -121.8863],
    ["San Francisco", 37.7749, -122.4194],
    ["Sacramento", 38.5816, -121.4944],
  ]),
  Colorado: make("Colorado", [
    ["Denver", 39.7392, -104.9903],
    ["Colorado Springs", 38.8339, -104.8214],
    ["Aurora", 39.7294, -104.8319],
    ["Boulder", 40.0150, -105.2705],
  ]),
  Connecticut: make("Connecticut", [
    ["Bridgeport", 41.1865, -73.1952],
    ["New Haven", 41.3083, -72.9279],
    ["Hartford", 41.7637, -72.6851],
    ["Stamford", 41.0534, -73.5387],
  ]),
  Delaware: make("Delaware", [
    ["Wilmington", 39.7391, -75.5398],
    ["Dover", 39.1582, -75.5244],
    ["Newark", 39.6837, -75.7497],
  ]),
  "District of Columbia": make("District of Columbia", [
    ["Washington", 38.9072, -77.0369],
  ]),
  Florida: make("Florida", [
    ["Jacksonville", 30.3322, -81.6557],
    ["Miami", 25.7617, -80.1918],
    ["Tampa", 27.9506, -82.4572],
    ["Orlando", 28.5383, -81.3792],
    ["St. Petersburg", 27.7676, -82.6403],
  ]),
  Georgia: make("Georgia", [
    ["Atlanta", 33.7490, -84.3880],
    ["Augusta", 33.4735, -82.0105],
    ["Savannah", 32.0809, -81.0912],
    ["Columbus", 32.4609, -84.9877],
  ]),
  Hawaii: make("Hawaii", [
    ["Honolulu", 21.3099, -157.8581],
    ["Hilo", 19.7297, -155.0900],
    ["Kailua", 21.3920, -157.7395],
  ]),
  Idaho: make("Idaho", [
    ["Boise", 43.6150, -116.2023],
    ["Idaho Falls", 43.4917, -112.0339],
    ["Coeur d'Alene", 47.6777, -116.7805],
  ]),
  Illinois: make("Illinois", [
    ["Chicago", 41.8781, -87.6298],
    ["Aurora", 41.7606, -88.3201],
    ["Naperville", 41.7508, -88.1535],
    ["Springfield", 39.7817, -89.6501],
  ]),
  Indiana: make("Indiana", [
    ["Indianapolis", 39.7684, -86.1581],
    ["Fort Wayne", 41.0793, -85.1394],
    ["Evansville", 37.9716, -87.5711],
    ["South Bend", 41.6764, -86.2520],
  ]),
  Iowa: make("Iowa", [
    ["Des Moines", 41.5868, -93.6250],
    ["Cedar Rapids", 41.9779, -91.6656],
    ["Davenport", 41.5236, -90.5776],
  ]),
  Kansas: make("Kansas", [
    ["Wichita", 37.6872, -97.3301],
    ["Overland Park", 38.9822, -94.6708],
    ["Kansas City", 39.1142, -94.6275],
    ["Topeka", 39.0473, -95.6752],
  ]),
  Kentucky: make("Kentucky", [
    ["Louisville", 38.2527, -85.7585],
    ["Lexington", 38.0406, -84.5037],
    ["Bowling Green", 36.9685, -86.4808],
  ]),
  Louisiana: make("Louisiana", [
    ["New Orleans", 29.9511, -90.0715],
    ["Baton Rouge", 30.4515, -91.1871],
    ["Shreveport", 32.5252, -93.7502],
    ["Lafayette", 30.2241, -92.0198],
  ]),
  Maine: make("Maine", [
    ["Portland", 43.6591, -70.2568],
    ["Bangor", 44.8016, -68.7712],
    ["Augusta", 44.3106, -69.7795],
  ]),
  Maryland: make("Maryland", [
    ["Baltimore", 39.2904, -76.6122],
    ["Annapolis", 38.9784, -76.4922],
    ["Frederick", 39.4143, -77.4105],
  ]),
  Massachusetts: make("Massachusetts", [
    ["Boston", 42.3601, -71.0589],
    ["Worcester", 42.2626, -71.8023],
    ["Springfield", 42.1015, -72.5898],
    ["Cambridge", 42.3736, -71.1097],
  ]),
  Michigan: make("Michigan", [
    ["Detroit", 42.3314, -83.0458],
    ["Grand Rapids", 42.9634, -85.6681],
    ["Ann Arbor", 42.2808, -83.7430],
    ["Lansing", 42.7325, -84.5555],
  ]),
  Minnesota: make("Minnesota", [
    ["Minneapolis", 44.9778, -93.2650],
    ["Saint Paul", 44.9537, -93.0900],
    ["Rochester", 44.0121, -92.4802],
    ["Duluth", 46.7867, -92.1005],
  ]),
  Mississippi: make("Mississippi", [
    ["Jackson", 32.2988, -90.1848],
    ["Gulfport", 30.3674, -89.0928],
    ["Biloxi", 30.3960, -88.8853],
  ]),
  Missouri: make("Missouri", [
    ["Kansas City", 39.0997, -94.5786],
    ["St. Louis", 38.6270, -90.1994],
    ["Springfield", 37.2090, -93.2923],
    ["Columbia", 38.9517, -92.3341],
  ]),
  Montana: make("Montana", [
    ["Billings", 45.7833, -108.5007],
    ["Missoula", 46.8721, -113.9940],
    ["Bozeman", 45.6770, -111.0429],
  ]),
  Nebraska: make("Nebraska", [
    ["Omaha", 41.2565, -95.9345],
    ["Lincoln", 40.8136, -96.7026],
  ]),
  Nevada: make("Nevada", [
    ["Las Vegas", 36.1699, -115.1398],
    ["Reno", 39.5296, -119.8138],
    ["Henderson", 36.0395, -114.9817],
    ["Carson City", 39.1638, -119.7674],
  ]),
  "New Hampshire": make("New Hampshire", [
    ["Manchester", 42.9956, -71.4548],
    ["Nashua", 42.7654, -71.4676],
    ["Concord", 43.2081, -71.5376],
  ]),
  "New Jersey": make("New Jersey", [
    ["Newark", 40.7357, -74.1724],
    ["Jersey City", 40.7178, -74.0431],
    ["Atlantic City", 39.3643, -74.4229],
    ["Trenton", 40.2206, -74.7597],
  ]),
  "New Mexico": make("New Mexico", [
    ["Albuquerque", 35.0844, -106.6504],
    ["Las Cruces", 32.3199, -106.7637],
    ["Santa Fe", 35.6870, -105.9378],
  ]),
  "New York": make("New York", [
    ["New York", 40.7128, -74.0060],
    ["Buffalo", 42.8864, -78.8784],
    ["Rochester", 43.1566, -77.6088],
    ["Albany", 42.6526, -73.7562],
    ["Syracuse", 43.0481, -76.1474],
  ]),
  "North Carolina": make("North Carolina", [
    ["Charlotte", 35.2271, -80.8431],
    ["Raleigh", 35.7796, -78.6382],
    ["Greensboro", 36.0726, -79.7920],
    ["Durham", 35.9940, -78.8986],
  ]),
  "North Dakota": make("North Dakota", [
    ["Fargo", 46.8772, -96.7898],
    ["Bismarck", 46.8083, -100.7837],
    ["Grand Forks", 47.9253, -97.0329],
  ]),
  Ohio: make("Ohio", [
    ["Columbus", 39.9612, -82.9988],
    ["Cleveland", 41.4993, -81.6944],
    ["Cincinnati", 39.1031, -84.5120],
    ["Toledo", 41.6528, -83.5379],
  ]),
  Oklahoma: make("Oklahoma", [
    ["Oklahoma City", 35.4676, -97.5164],
    ["Tulsa", 36.1539, -95.9928],
    ["Norman", 35.2226, -97.4395],
  ]),
  Oregon: make("Oregon", [
    ["Portland", 45.5152, -122.6784],
    ["Salem", 44.9429, -123.0351],
    ["Eugene", 44.0521, -123.0868],
    ["Bend", 44.0582, -121.3153],
  ]),
  Pennsylvania: make("Pennsylvania", [
    ["Philadelphia", 39.9526, -75.1652],
    ["Pittsburgh", 40.4406, -79.9959],
    ["Harrisburg", 40.2732, -76.8867],
    ["Allentown", 40.6084, -75.4902],
  ]),
  "Rhode Island": make("Rhode Island", [
    ["Providence", 41.8240, -71.4128],
    ["Newport", 41.4901, -71.3128],
    ["Warwick", 41.7001, -71.4162],
  ]),
  "South Carolina": make("South Carolina", [
    ["Charleston", 32.7765, -79.9311],
    ["Columbia", 34.0007, -81.0348],
    ["Myrtle Beach", 33.6891, -78.8867],
    ["Greenville", 34.8526, -82.3940],
  ]),
  "South Dakota": make("South Dakota", [
    ["Sioux Falls", 43.5446, -96.7311],
    ["Rapid City", 44.0805, -103.2310],
    ["Pierre", 44.3683, -100.3510],
  ]),
  Tennessee: make("Tennessee", [
    ["Nashville", 36.1627, -86.7816],
    ["Memphis", 35.1495, -90.0490],
    ["Knoxville", 35.9606, -83.9207],
    ["Chattanooga", 35.0456, -85.3097],
  ]),
  Texas: make("Texas", [
    ["Houston", 29.7604, -95.3698],
    ["San Antonio", 29.4241, -98.4936],
    ["Dallas", 32.7767, -96.7970],
    ["Austin", 30.2672, -97.7431],
    ["Fort Worth", 32.7555, -97.3308],
  ]),
  Utah: make("Utah", [
    ["Salt Lake City", 40.7608, -111.8910],
    ["West Valley City", 40.6916, -112.0011],
    ["Provo", 40.2338, -111.6585],
    ["Park City", 40.6461, -111.4980],
  ]),
  Vermont: make("Vermont", [
    ["Burlington", 44.4759, -73.2121],
    ["Montpelier", 44.2601, -72.5754],
  ]),
  Virginia: make("Virginia", [
    ["Virginia Beach", 36.8529, -75.9780],
    ["Norfolk", 36.8508, -76.2859],
    ["Richmond", 37.5407, -77.4360],
    ["Arlington", 38.8816, -77.0910],
  ]),
  Washington: make("Washington", [
    ["Seattle", 47.6062, -122.3321],
    ["Spokane", 47.6588, -117.4260],
    ["Tacoma", 47.2529, -122.4443],
    ["Olympia", 47.0379, -122.9007],
  ]),
  "West Virginia": make("West Virginia", [
    ["Charleston", 38.3498, -81.6326],
    ["Huntington", 38.4192, -82.4452],
    ["Morgantown", 39.6295, -79.9559],
  ]),
  Wisconsin: make("Wisconsin", [
    ["Milwaukee", 43.0389, -87.9065],
    ["Madison", 43.0731, -89.4012],
    ["Green Bay", 44.5133, -88.0133],
  ]),
  Wyoming: make("Wyoming", [
    ["Cheyenne", 41.1400, -104.8202],
    ["Casper", 42.8666, -106.3131],
    ["Jackson", 43.4799, -110.7624],
  ]),
  "Puerto Rico": make("Puerto Rico", [
    ["San Juan", 18.4655, -66.1057],
    ["Bayamón", 18.3985, -66.1557],
    ["Ponce", 18.0111, -66.6141],
    ["Mayagüez", 18.2011, -67.1397],
  ]),
};

// Build a case-insensitive lookup with common alias support (e.g., "DC" → "District of Columbia").
const ALIASES = {
  dc: "District of Columbia",
  "d.c.": "District of Columbia",
  "washington dc": "District of Columbia",
  "washington d.c.": "District of Columbia",
  "puerto rico": "Puerto Rico",
  pr: "Puerto Rico",
};

const STATE_LOOKUP = (() => {
  const m = new Map();
  Object.keys(STATE_MAJOR_CITIES).forEach((k) => m.set(k.toLowerCase(), k));
  Object.entries(ALIASES).forEach(([alias, target]) => m.set(alias, target));
  return m;
})();

/**
 * If `query` is a US state name (or alias), return that state's major-city
 * objects shaped like Open-Meteo geocoding results. Otherwise return [].
 */
export function findStateCities(query) {
  if (!query) return [];
  const key = query.trim().toLowerCase();
  const stateName = STATE_LOOKUP.get(key);
  if (!stateName) return [];
  return STATE_MAJOR_CITIES[stateName] || [];
}
